'use client';

/**
 * "Create" dialog — the create counterpart to the Remix dialog, and
 * deliberately the same object: one kind toggle (App / Agent), then the same
 * environment picker (your Robutler agent + external AI coding tools).
 *
 *  • Robutler agent → drops the caller's OWN personal agent on the canvas,
 *    prefilled to build the new app / agent with you in-app. It used to drop
 *    @robutler.factory; that agent is retired (2026-09-05) and the personal
 *    agent builds apps and agents itself. The handle comes from
 *    `GET /api/agents/personal` (never `${username}.my-robutler`, which
 *    dangles after a rename and names nothing for a guest), and it is
 *    resolved BEFORE `addItem` so a failed lookup writes nothing. A guest is
 *    sent to the signup dialog; any other failure is shown inline and the
 *    dialog stays open.
 *  • Claude Code / Codex / Cursor / Antigravity → fires that tool's deep-link
 *    with a ready build prompt, with a copy-the-prompt fallback.
 *
 * REMIX FIRST. Creating from nothing is the worst of the three outcomes for
 * most requests (the app already exists / one is close enough to remix /
 * nothing fits), so the dialog says so above the fold and offers a one-click
 * way back to browsing. The prompts carry the same instruction to the coding
 * agent, and `/CREATE.md` is the playbook they enforce: survey the catalog
 * with widget_catalog, read the closest bundles with widget_download, remix
 * when one is close, and reuse `/widgets/shared/*` instead of re-writing it.
 *
 * Event-driven: call `openCreateWithDialog()` (optionally with a kind) from
 * anywhere; the single instance mounted in the workspace shell renders it.
 */

import { createPortal } from 'react-dom';
import { useT } from '@/components/i18n-provider';
import { useCallback, useEffect, useState } from 'react';
import { Check, Compass, Copy, ExternalLink, Loader2, X } from 'lucide-react';
import { BotIcon } from '@/components/icons/bot-icon';
import { cn } from '@/lib/utils';
import { useWorkspaceActions } from '@/components/workspace/store';
import { openDiscover } from '@/components/workspace/discover/discover-events';
import { useFullscreenNav } from '@/components/workspace/fullscreen-nav';
import { fetchPersonalAgent } from '@/hooks/use-personal-agent';
import { CODING_ENVS, launchCodingEnv, type CodingEnv, type EnvId } from './coding-env-marks';

type CreateKind = 'app' | 'agent';

const EVENT_NAME = 'workspace:open-create-with';

/**
 * Imperative opener, fired from the Discover header "+ Create", the panel "+"
 * buttons and the search Commands. `kind` only seeds the toggle: the user can
 * flip it inside the dialog, so callers that don't know (a generic Create
 * button) simply omit it and get App.
 */
export function openCreateWithDialog(kind?: CreateKind): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { kind } }));
}

function envSub(env: CodingEnv, kind: CreateKind): string {
  const noun = kind === 'app' ? 'app' : 'agent';
  switch (env.id) {
    case 'robutler':
      return `Build your ${noun} here with your Robutler agent`;
    case 'claude':
      return 'Opens a Code session in Claude Desktop';
    case 'codex':
      return 'Opens a new Codex thread';
    case 'antigravity':
      return 'Opens Google Antigravity';
    case 'cursor':
      return 'Opens Cursor with the prompt';
    default:
      return '';
  }
}

/**
 * The prompt handed to an external coding tool. Its whole job is to stop the
 * tool from opening a blank file: survey first, remix when something is close,
 * reuse the shared modules, and only scaffold when the survey comes up empty.
 * `/CREATE.md` carries the full playbook; these lines make sure it gets read
 * before any code is written.
 */
function buildCreatePrompt(kind: CreateKind): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://robutler.ai';
  if (kind === 'app') {
    return [
      'Help me create a new Robutler app.',
      '',
      `1) Connect the Robutler MCP server (Streamable HTTP, OAuth, authorize when prompted): ${origin}/mcp`,
      `2) Read ${origin}/CREATE.md before writing any code. It is the contract for this task.`,
      '3) Run its section 2 survey FIRST: list what already exists with widget_catalog, search the marketplace, then read the closest two or three bundles in full with widget_download (their AGENT.md or README first).',
      `4) Then choose out loud. If an app already does this, tell me instead of building a duplicate. If one is close, remix it with widget_remix and follow ${origin}/REMIX.md, which is the preferred path. Scaffold from nothing only when the survey says nothing fits.`,
      '5) Reuse before you write. The shared modules at /widgets/shared/ already cover collab and documents, netcode, leaderboards, WebRTC, printing, fonts, geometry and testing, and collab-board, docs, vector-editor and tank-arena are the reference implementations to copy the shape from.',
      '6) Report what the survey found, then ask me what the app should do.',
    ].join('\n');
  }
  return [
    'Help me create a new Robutler agent.',
    '',
    `1) Connect the Robutler MCP server (Streamable HTTP, OAuth, authorize when prompted): ${origin}/mcp`,
    `2) Read ${origin}/CREATE-AGENT.md before creating anything. It is the contract for this task.`,
    '3) Check what exists first: list_my_agents shows my own agents including private ones (search cannot see those), and search covers the public catalog. If one already fits, update_agent it or delegate to it instead of creating a second.',
    '4) Create it with the create_agent MCP tool. Get the trust rules right: accept_from is inbound AND discoverability (everyone means publicly listed), talk_to is an outbound allowlist an agent cannot reach past, so every collaborator goes in talk_to AND in the instructions.',
    '5) If the agent needs an app to drive, run the CREATE.md section 2 survey for that app too and prefer remixing an existing one over building new.',
    '6) Then ask me what the agent should do, who may call it, and which tools it needs. Test it with delegate and iterate with update_agent rather than making another one.',
  ].join('\n');
}

export function CreateWithDialog() {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<CreateKind>('app');
  const [busy, setBusy] = useState<EnvId | null>(null);
  const [panel, setPanel] = useState<{ env: CodingEnv; prompt: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { addItem } = useWorkspaceActions();
  const { openHref } = useFullscreenNav();

  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<{ kind?: CreateKind }>).detail;
      setKind(detail?.kind === 'agent' ? 'agent' : 'app');
      setPanel(null);
      setBusy(null);
      setCopied(false);
      setErr(null);
      setOpen(true);
    };
    window.addEventListener(EVENT_NAME, onOpen);
    return () => window.removeEventListener(EVENT_NAME, onOpen);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const close = useCallback(() => setOpen(false), []);

  /** The remix-first escape hatch: back to Discover, where the catalog is. */
  const browseApps = useCallback(() => {
    close();
    openDiscover();
  }, [close]);

  /**
   * The direct path for an agent: the create-agent dialog (`/create-agent`),
   * which makes a private agent under a name you choose and hands you its
   * settings. Until 2026-09-27 the only way to reach it while signed in was
   * to know the URL; this dialog offered the agent kind nothing but the
   * app's copy and the coding-tool list (e2e report, handoff 2026-09-26).
   */
  const newAgentDirect = useCallback(() => {
    close();
    openHref('/create-agent');
  }, [close, openHref]);

  const routeToPersonalAgent = useCallback(async () => {
    if (busy) return;
    setBusy('robutler');
    setErr(null);
    try {
      const lookup = await fetchPersonalAgent();
      if (!lookup.agent) {
        if (lookup.guest) {
          close();
          openHref('/signup');
          return;
        }
        setErr(t('personalAgent.notReady'));
        return;
      }
      const agent = lookup.agent;
      await addItem({
        type: 'agent',
        refId: agent.id,
        state: {
          width: 450,
          height: 500,
          cache: {
            username: agent.username,
            displayName: agent.displayName ?? agent.username,
            avatarUrl: agent.avatarUrl ?? null,
            prefillText:
              kind === 'app'
                ? t('workspace.createWith.prefillApp')
                : t('workspace.createWith.prefillAgent'),
          },
        },
      });
      close();
    } catch {
      setErr(t('personalAgent.openFailed'));
    } finally {
      setBusy(null);
    }
  }, [addItem, busy, close, kind, openHref, t]);

  /** Fire the tool's deep link and copy the prompt, both inside the click that
   *  called this. Deep link first: see `launchCodingEnv` for the Safari
   *  failure the old copy-then-open order produced. */
  const launch = useCallback((env: CodingEnv, prompt: string) => {
    const launched = launchCodingEnv(env, prompt);
    if (!launched) return null;
    void launched.copied.then((ok) => { if (ok) setCopied(true); });
    return launched;
  }, []);

  // Synchronous on purpose: nothing may run between the click and the deep
  // link (`launchCodingEnv`). A refused copy is covered by the panel's Copy
  // button.
  const pick = useCallback(
    (env: CodingEnv) => {
      if (env.id === 'robutler') {
        void routeToPersonalAgent();
        return;
      }
      if (!env.scheme || busy) return;
      setErr(null);
      const prompt = buildCreatePrompt(kind);
      const launched = launch(env, prompt);
      if (launched) setPanel({ env, prompt, url: launched.url });
    },
    [busy, kind, launch, routeToPersonalAgent],
  );

  const copyPrompt = useCallback(async () => {
    if (!panel) return;
    try {
      await navigator.clipboard.writeText(panel.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  }, [panel]);

  if (!open || typeof document === 'undefined') return null;

  // One dialog for both kinds, so the header names the verb and the toggle
  // below names the object. Once an env is picked the header switches to the
  // concrete "New app with Cursor" so the launch state is unambiguous.
  // Whole sentences from en.json: the English "with" glued between two
  // translated halves read "Neuer Agent with Cursor" (final-portal-ui,
  // 2026-09-28).
  const title = panel
    ? kind === 'app'
      ? t('workspace.createWith.newAppWith', { env: panel.env.name })
      : t('workspace.createWith.newAgentWith', { env: panel.env.name })
    : t('actions.create');

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" onClick={close}>
      <div
        className="w-full max-w-sm overflow-hidden rounded-2xl border border-border bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold">{title}</h2>
          </div>
          <button
            onClick={close}
            aria-label={t('workspace.canvas.node.close')}
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-muted-foreground hover:bg-accent"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {!panel && (
          // What you are making, then the honest recommendation. Remix is the
          // better outcome for most requests, so it gets a real affordance
          // here rather than a footnote nobody reads.
          <div className="space-y-2.5 border-b border-border px-4 py-3">
            <div className="flex gap-0.5 rounded-lg bg-foreground/[0.05] p-0.5">
              {([
                ['app', t('workspace.canvas.widget')],
                ['agent', t('workspace.canvas.agent')],
              ] as const).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={kind === value}
                  onClick={() => setKind(value)}
                  className={cn(
                    'flex-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
                    kind === value
                      ? 'bg-background text-foreground shadow-sm ring-1 ring-border/50'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            {/* Each kind gets its own line. Remix-first is an APP truth (the
                catalog is full of near-misses); an agent is made from its
                settings, and the line says so instead of repeating the app's
                advice under an Agent toggle. */}
            <p className="text-xs leading-relaxed text-muted-foreground">
              {kind === 'agent'
                ? t('workspace.createWith.agentIntro')
                : t('workspace.createWith.remixFirst')}
            </p>
            {kind === 'app' && (
              <button
                type="button"
                onClick={browseApps}
                className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-accent"
              >
                <Compass className="h-3.5 w-3.5" />
                {t('workspace.createWith.browseApps')}
              </button>
            )}
          </div>
        )}

        {!panel ? (
          <div className="p-2">
            {/* `pickWhere` was written as a subtitle sentence, so it carries a
                full stop. It reads as the eyebrow over the env list now, and an
                eyebrow with a period looks like a typo: strip the terminal
                punctuation (Latin + CJK) rather than forking 15 translations. */}
            <p className="px-3 pb-1 pt-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t('workspace.createWith.pickWhere').replace(/[.。｡]\s*$/, '')}
            </p>
            {/* First row for an agent: make one here, now. Same anatomy as
                the environment rows below it so it reads as the plainest of
                the choices rather than as extra chrome. */}
            {kind === 'agent' && (
              <button
                type="button"
                data-testid="create-with-new-agent"
                onClick={newAgentDirect}
                disabled={!!busy}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-accent disabled:opacity-60"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
                  <BotIcon className="h-4.5 w-4.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{t('workspace.createWith.newAgentDirect')}</span>
                  {/* Wraps instead of truncating: in this `max-w-sm` dialog
                      the sentence was cut off before "its settings", the
                      part that says where the agent is set up (portal
                      visual pass, 2026-09-28). */}
                  <span className="block text-xs leading-snug text-muted-foreground" data-testid="create-with-new-agent-desc">
                    {t('workspace.createWith.newAgentDirectDesc')}
                  </span>
                </span>
              </button>
            )}
            {CODING_ENVS.map((env) => (
              <button
                key={env.id}
                onClick={() => void pick(env)}
                disabled={!!busy}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-accent disabled:opacity-60"
              >
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${env.chip}`}>
                  {env.logo}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{env.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{envSub(env, kind)}</span>
                </span>
                {busy === env.id ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
                ) : env.id !== 'robutler' ? (
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                ) : null}
              </button>
            ))}
            {err && <p className="px-3 py-2 text-xs text-red-500">{err}</p>}
          </div>
        ) : (
          <div className="space-y-3 p-4">
            <p className="text-xs text-muted-foreground">
              Opening <span className="font-medium text-foreground">{panel.env.name}</span>…
              {copied ? ' The prompt is in your clipboard. If it opened without it, just paste.' : ''}{' '}
              If nothing happens, make sure it&apos;s installed, then paste the prompt after connecting the MCP.
            </p>
            <pre className="max-h-44 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-muted/40 p-3 text-[11px] leading-relaxed text-foreground">
              {panel.prompt}
            </pre>
            <div className="flex gap-2">
              <button
                onClick={() => void copyPrompt()}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium hover:bg-accent"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? t('settings.copied') : t('workspace.createWith.copyPrompt')}
              </button>
              <button
                onClick={() => launch(panel.env, panel.prompt)}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-foreground px-3 py-2 text-xs font-semibold text-background hover:opacity-90"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open {panel.env.name}
              </button>
            </div>
            <button
              onClick={() => setPanel(null)}
              className="w-full text-center text-xs text-muted-foreground hover:text-foreground"
            >
              {t('workspace.chooseDifferentEnv')}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
